import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { useGameStore } from '../store/gameStore';

const KEY = 'cellsnake:seenFirstPlayTutorial';

// Hebrew tutorial — most of the player base is Hebrew-speaking. Keeping
// the structure (4 short tips) so the read-time stays well under 30 s.
// Step 4 (avoid self-collision) was added after the analytics dashboard
// showed a meaningful share of deaths in the "self / env" bucket — the
// most common reason new players die in their first life is running
// their own head into their tail at full speed.
// English — matches the site-wide language decision (game UI, guides
// and blog are all English; the tutorial was the last Hebrew holdout).
const STEPS: ReadonlyArray<{ icon: string; title: string; body: string }> = [
  {
    icon: '🖱️',
    title: 'Move the mouse to steer',
    body: 'Your snake follows the cursor. On mobile, drag the joystick in the lower half of the screen.',
  },
  {
    icon: '⚡',
    title: 'Space = boost',
    body: 'Hold Space to sprint. It burns mass — save it for escapes and chases.',
  },
  {
    icon: '◉',
    title: 'Eat your own color',
    body: 'Pellets matching your color are worth ×2.5. Everything else is a distraction.',
  },
  {
    icon: '⚠',
    title: "Don't hit other snakes",
    body: 'If your head touches any body — including near your own tail on sharp turns — you die. Turn wide, especially while boosting.',
  },
];

/**
 * First-time tutorial — shown once per device, dismissible at any step.
 * Drops in over the HUD on the first game-canvas mount, blocking nothing
 * but the modal area. Doesn't pause the game (the snake auto-steers to
 * the cursor so players can already drive around while reading).
 *
 * The localStorage flag is the same shape as TutorialHint on landing,
 * so a returning visitor never re-sees the onboarding flow.
 */
export function FirstPlayTutorial() {
  const playerHue = useGameStore((s) => s.hud.playerHue);
  const alive = useGameStore((s) => s.hud.alive);
  const [show, setShow] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    try {
      if (!localStorage.getItem(KEY)) setShow(true);
    } catch {
      /* private mode — never persists, but UX is the same */
    }
  }, []);

  const dismiss = (): void => {
    setShow(false);
    try { localStorage.setItem(KEY, '1'); } catch { /* noop */ }
  };

  const next = (): void => {
    if (step < STEPS.length - 1) setStep(step + 1);
    else dismiss();
  };

  // Hide automatically if the player dies before finishing — death takes
  // priority + we'll surface the tutorial again on their next spawn.
  useEffect(() => {
    if (!alive) setShow(false);
  }, [alive]);

  const current = STEPS[step]!;
  const color = `hsl(${playerHue} 100% 70%)`;

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          key={step}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.28 }}
          className="absolute top-24 left-1/2 -translate-x-1/2 z-30 pointer-events-auto"
        >
          <div
            className="hud-panel relative px-5 py-4 min-w-[280px] max-w-[340px] text-center"
            style={{ boxShadow: `0 0 24px ${color}33` }}
            dir="rtl"
          >
            <div className="flex items-center justify-center gap-3 mb-2">
              <span
                className="text-2xl"
                style={{ color, filter: `drop-shadow(0 0 6px ${color})` }}
                aria-hidden
              >
                {current.icon}
              </span>
              <span
                className="text-[10px] uppercase tracking-[0.3em]"
                style={{ color }}
              >
                {step + 1} / {STEPS.length}
              </span>
            </div>
            <div className="text-base font-semibold text-white mb-1">
              {current.title}
            </div>
            <p className="text-[12px] text-white/65 leading-relaxed mb-4">
              {current.body}
            </p>
            <div className="flex items-center justify-center gap-3">
              {step < STEPS.length - 1 ? (
                <button
                  onClick={next}
                  className="px-3.5 py-1.5 rounded text-[10px] uppercase tracking-[0.2em] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/40"
                  style={{
                    color,
                    background: `${color}22`,
                    border: `1px solid ${color}66`,
                  }}
                >
                  Next →
                </button>
              ) : (
                <button
                  onClick={dismiss}
                  className="px-3.5 py-1.5 rounded text-[10px] uppercase tracking-[0.2em] text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/40"
                  style={{
                    background: color,
                    border: `1px solid ${color}`,
                  }}
                >
                  Got it
                </button>
              )}
              <button
                onClick={dismiss}
                className="text-[10px] uppercase tracking-[0.2em] text-white/45 hover:text-white/75 transition-colors"
              >
                Skip
              </button>
            </div>
            {/* Step dots. */}
            <div className="flex items-center justify-center gap-1.5 mt-3">
              {STEPS.map((_, i) => (
                <span
                  key={i}
                  className="w-1.5 h-1.5 rounded-full transition-all"
                  style={{
                    background: i === step ? color : 'rgba(255,255,255,0.18)',
                    transform: i === step ? 'scale(1.4)' : 'scale(1)',
                  }}
                />
              ))}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
